<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div
        v-if="showBanner"
        class="cookie-banner"
        role="dialog"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
      >
        <v-container class="py-4">
          <v-row
            align="center"
            justify="space-between"
          >
            <!-- Contenido informativo -->
            <v-col
              cols="12"
              md="8"
              lg="9"
            >
              <div class="cookie-content">
                <h2
                  id="cookie-banner-title"
                  class="cookie-title"
                >
                  <v-icon
                    class="mr-2"
                    size="small"
                  >
                    mdi-cookie
                  </v-icon>
                  {{ t("cookies.banner.titulo") }}
                </h2>
                <p
                  id="cookie-banner-description"
                  class="cookie-description"
                >
                  {{ t("cookies.banner.descripcion") }}
                  <a
                    href="#"
                    class="cookie-link"
                    @click.prevent="goToPoliticaCookies"
                  >
                    {{ t("cookies.banner.masInfo") }}
                  </a>
                </p>
              </div>
            </v-col>

            <!-- Botones de acción -->
            <v-col
              cols="12"
              md="4"
              lg="3"
            >
              <div class="cookie-actions">
                <v-btn
                  color="primary"
                  variant="flat"
                  block
                  class="mb-2"
                  :aria-label="t('cookies.banner.ariaAceptar')"
                  @click="acceptAll"
                >
                  {{ t("cookies.banner.aceptarTodo") }}
                </v-btn>

                <v-btn
                  color="white"
                  variant="elevated"
                  block
                  class="mb-2 config-btn"
                  :aria-label="t('cookies.banner.ariaConfigurar')"
                  @click="openConfig"
                >
                  {{ t("cookies.banner.configurar") }}
                </v-btn>

                <v-btn
                  variant="text"
                  block
                  class="reject-btn"
                  :aria-label="t('cookies.banner.ariaRechazar')"
                  @click="rejectAll"
                >
                  {{ t("cookies.banner.rechazar") }}
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </Transition>

    <!-- Modal de configuración -->
    <CookieConfig
      v-model="showConfigModal"
      :preferences="preferences"
      @save="savePreferences"
      @accept-all="handleAcceptAll"
      @reject-all="handleRejectAll"
    />
  </Teleport>
</template>

<script setup>
import { computed } from "vue";
import { useCookieConsent } from "@/composables/useCookieConsent";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { getLocaleFromRoute } from "@/router";
import CookieConfig from "./CookieConfig.vue";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// Obtener locale actual
const currentLocale = computed(() => getLocaleFromRoute(route));

// Helper para navegar manteniendo el idioma
const navigateTo = (baseName, options = {}) => {
  const routeName = currentLocale.value === "en" ? `${baseName}-en` : baseName;
  router.push({ name: routeName, ...options });
};

const {
  showBanner,
  showConfigModal,
  preferences,
  acceptAll,
  rejectAll,
  openConfigModal,
  saveCustomPreferences,
} = useCookieConsent();

const openConfig = () => {
  openConfigModal();
};

const savePreferences = (newPreferences) => {
  Object.assign(preferences.value, newPreferences);
  saveCustomPreferences();
};

const handleAcceptAll = (newPreferences) => {
  Object.assign(preferences.value, newPreferences);
  acceptAll();
};

const handleRejectAll = (newPreferences) => {
  Object.assign(preferences.value, newPreferences);
  rejectAll();
};

const goToPoliticaCookies = () => {
  navigateTo("politicaCookies");
};
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, #3a5a6d 100%);
  color: white;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  z-index: 9999;
  border-top: 3px solid #c83d2d;
}

.cookie-content {
  padding-right: 16px;
}

.cookie-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  color: white;
}

.cookie-description {
  font-size: 0.9375rem;
  line-height: 1.6;
  margin-bottom: 0;
  color: rgba(255, 255, 255, 0.95);
}

.cookie-link {
  color: #ffc107;
  text-decoration: underline;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.cookie-link:hover {
  opacity: 0.8;
}

.cookie-link:focus-visible {
  outline: 2px solid #ffc107;
  outline-offset: 2px;
  border-radius: 2px;
}

.cookie-actions {
  display: flex;
  flex-direction: column;
}

.config-btn {
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 600 !important;
}

.reject-btn {
  color: rgba(255, 255, 255, 0.8) !important;
  font-size: 0.875rem !important;
}

.reject-btn:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
  color: white !important;
}

/* Animación de entrada */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* Responsive */
@media (max-width: 960px) {
  .cookie-content {
    padding-right: 0;
    margin-bottom: 16px;
  }

  .cookie-title {
    font-size: 1rem;
  }

  .cookie-description {
    font-size: 0.875rem;
  }

  .cookie-banner {
    max-height: 90vh;
    overflow-y: auto;
  }
}

@media (max-width: 600px) {
  .cookie-banner {
    border-top-width: 2px;
    max-height: 85vh;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }

  .cookie-banner .v-container {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  .cookie-title {
    font-size: 0.9375rem;
  }

  .cookie-description {
    font-size: 0.8125rem;
    line-height: 1.5;
  }

  .cookie-actions .v-btn {
    min-height: 44px;
  }

  .cookie-actions .mb-2 {
    margin-bottom: 12px !important;
  }

  .cookie-content {
    margin-bottom: 12px !important;
  }
}
</style>
